import {
    useCallback,
    useEffect,
    useState
} from "react";
import {
    useWindowDimensions,
    ActivityIndicator,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    TextInput,
    Dialog,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useNavigation
} from "@react-navigation/native";
import type RootStackParamList from "../../../../navigation/type";
import type {
    GetAllNewsCategoriesResponseType
} from "../../../../services/restAPI/actions/getAllNewsCategories/type";
import {
    ArrowLeftIcon,
    SearchIcon,
    PencilIcon,
    Trash2Icon,
    PlusIcon
} from "lucide-react-native";
import {
    type NativeStackNavigationProp
} from "@react-navigation/native-stack";
import RESTServiceContext from "../../../../services/restAPI/context";
import syncSystemLocales from "../../../../utils/syncLocales";
import Pagination from "../../../../components/pagination";

const SettingsNewsCategories = () => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext();
    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        categories,
        setCategories
    ] = useState<GetAllNewsCategoriesResponseType["payload"]>([]);

    const [
        isLoading,
        setIsLoading
    ] = useState<boolean>(true);

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState<boolean>(false);

    const [
        isCreateDialogVisible,
        setIsCreateDialogVisible
    ] = useState<boolean>(false);

    const [
        newCategoryTitleTR,
        setNewCategoryTitleTR
    ] = useState<string>("");

    const [
        newCategoryTitleEN,
        setNewCategoryTitleEN
    ] = useState<string>("");

    const [
        newCategoryKey,
        setNewCategoryKey
    ] = useState<string>("");

    const [
        isDeleteDialogVisible,
        setIsDeleteDialogVisible
    ] = useState<boolean>(false);

    const [
        selectedCategoryID,
        setSelectedCategoryID
    ] = useState<string | null>(null);

    const [
        isEditDialogVisible,
        setIsEditDialogVisible
    ] = useState<boolean>(false);

    const [
        editingCategoryTitleTR,
        setEditingCategoryTitleTR
    ] = useState<string>("");

    const [
        editingCategoryTitleEN,
        setEditingCategoryTitleEN
    ] = useState<string>("");

    const [
        editingCategoryKey,
        setEditingCategoryKey
    ] = useState<string>("");

    const [
        searchQuery,
        setSearchQuery
    ] = useState<string>("");

    const [
        page,
        setPage
    ] = useState<number>(1);

    const [
        totalCount,
        setTotalCount
    ] = useState<number>(0);

    const fetchCategories = useCallback(async (currentPage: number = 1) => {
        try {
            setIsLoading(true);
            const requestParams: Record<string, string | number> = {
                page: currentPage
            };

            if (searchQuery) {
                requestParams.search = searchQuery;
            }

            const response = await RESTServiceContext.actions.getAllNewsCategories(requestParams);
            if (response.code === 200) {
                setCategories(response.payload);
                if (response.totalCount !== undefined) {
                    setTotalCount(response.totalCount);
                }
            } else {
                setCategories([]);
                setTotalCount(0);
            }
        } catch (error) {
            console.error(error);
            NCoreUIKitSnackBar.open({
                title: localize("error-fetching-data")
            });
        } finally {
            setIsLoading(false);
        }
    }, [
        searchQuery,
        localize
    ]);

    useEffect(() => {
        fetchCategories(1);
        setPage(1);
    }, [
        fetchCategories
    ]);

    const handleCreate = async () => {
        if(!newCategoryTitleTR.trim() || !newCategoryTitleEN.trim() || !newCategoryKey.trim()) {
            NCoreUIKitSnackBar.open({
                title: localize("error-empty-field")
            });
            return;
        }

        try {
            setIsSubmitting(true);
            const response = await RESTServiceContext.actions.createNewsCategory({
                titleKey: `news_category_${newCategoryKey.trim()}`,
                titleEN: newCategoryTitleEN.trim(),
                titleTR: newCategoryTitleTR.trim(),
                key: newCategoryKey.trim()
            });

            if(response.code === 200 || response.code === 201) {
                NCoreUIKitSnackBar.open({
                    title: localize("success-create")
                });
                setIsCreateDialogVisible(false);
                setNewCategoryTitleTR("");
                setNewCategoryTitleEN("");
                setNewCategoryKey("");
                fetchCategories(page);
                syncSystemLocales(activeLocale);
            } else {
                NCoreUIKitSnackBar.open({
                    title: response.message || localize("error-create")
                });
            }
        } catch (error) {
            console.error(error);
            NCoreUIKitSnackBar.open({
                title: localize("error-create")
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if(!selectedCategoryID) {
            return;
        }

        try {
            setIsSubmitting(true);
            const response = await RESTServiceContext.actions.deleteNewsCategory({
                id: selectedCategoryID
            });

            if(response.code === 200) {
                NCoreUIKitSnackBar.open({
                    title: localize("success-delete")
                });
                setIsDeleteDialogVisible(false);
                setSelectedCategoryID(null);
                fetchCategories(page);
                syncSystemLocales(activeLocale);
            } else {
                NCoreUIKitSnackBar.open({
                    title: response.message || localize("error-delete")
                });
            }
        } catch (error) {
            console.error(error);
            NCoreUIKitSnackBar.open({
                title: localize("error-delete")
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEdit = async () => {
        if(!selectedCategoryID || !editingCategoryTitleTR.trim() || !editingCategoryTitleEN.trim() || !editingCategoryKey.trim()) {
            NCoreUIKitSnackBar.open({
                title: localize("error-empty-field")
            });
            return;
        }

        try {
            setIsSubmitting(true);
            const response = await RESTServiceContext.actions.updateNewsCategory({
                titleKey: `news_category_${editingCategoryKey.trim()}`,
                titleEN: editingCategoryTitleEN.trim(),
                titleTR: editingCategoryTitleTR.trim(),
                key: editingCategoryKey.trim(),
                id: selectedCategoryID
            });

            if(response.code === 200) {
                NCoreUIKitSnackBar.open({
                    title: localize("success-edit")
                });
                setIsEditDialogVisible(false);
                setSelectedCategoryID(null);
                setEditingCategoryTitleTR("");
                setEditingCategoryTitleEN("");
                setEditingCategoryKey("");
                fetchCategories(page);
                syncSystemLocales(activeLocale);
            } else {
                NCoreUIKitSnackBar.open({
                    title: response.message || localize("error-edit")
                });
            }
        } catch (error) {
            console.error(error);
            NCoreUIKitSnackBar.open({
                title: localize("error-edit")
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderHeader = () => {
        return <View
            style={stylesheet.headerContainer}
        >
            <View
                style={[
                    stylesheet.headerTitleContainer,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Button
                    icon={() => <ArrowLeftIcon
                        color={colors.project.brandWhite}
                        size={24}
                    />}
                    customBorderColor="secondaryButton"
                    onPress={() => navigation.goBack()}
                    customColor="secondaryButton"
                    spreadBehaviour="free"
                    variant="filled"
                />
                <Text
                    variant="titleLargeSize"
                    style={{
                        flexShrink: 1
                    }}
                >
                    {localize("menu-settings-news-categories")}
                </Text>
            </View>
            {!isMobile && <Button
                onPress={() => setIsCreateDialogVisible(true)}
                title={localize("create-new-category")}
                icon={({
                    color,
                    size
                }) => <PlusIcon
                    color={colors.content.icon[color]}
                    size={size}
                />}
                spreadBehaviour="free"
                variant="filled"
            />}
        </View>;
    };

    const renderFilters = () => {
        return <View
            style={{
                marginTop: spaces.spacingLg,
                gap: spaces.spacingMd
            }}
        >
            {isMobile && <Button
                onPress={() => setIsCreateDialogVisible(true)}
                title={localize("create-new-category")}
                icon={({
                    color,
                    size
                }) => <PlusIcon
                    color={colors.content.icon[color]}
                    size={size}
                />}
                spreadBehaviour="stretch"
                variant="filled"
            />}
            <TextInput
                placeholder={localize("search") || "Kategori ara..."}
                customColor={colors.project.brandWhite}
                icon={({
                    color,
                    size
                }) => <SearchIcon
                    color={colors.content.icon[color]}
                    size={size}
                />}
                onChangeText={setSearchQuery}
                spreadBehaviour="stretch"
                value={searchQuery}
            />
        </View>;
    };

    const renderCreateDialog = () => {
        return <Dialog
            contentContainerStyle={stylesheet.dialogContentContainer}
            secondaryButtonProps={{
                onPress: () => setIsCreateDialogVisible(false),
                customTextColor: colors.project.brandWhite,
                customBorderColor: "secondaryButton",
                title: localize("cancel") || "İptal",
                customColor: "secondaryButton",
                variant: "filled"
            }}
            onClosed={() => setIsCreateDialogVisible(false)}
            primaryButtonProps={{
                title: localize("save") || "Kaydet",
                isLoading: isSubmitting,
                onPress: handleCreate
            }}
            title={localize("create-new-category")}
            isVisible={isCreateDialogVisible}
            variant="yes-no"
        >
            <View
                style={[
                    stylesheet.dialogForm,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <TextInput
                    placeholder={`${localize("category-name-placeholder")} (TR)`}
                    title={`${localize("category-name")} (TR)`}
                    customColor={colors.project.brandWhite}
                    onChangeText={setNewCategoryTitleTR}
                    value={newCategoryTitleTR}
                    spreadBehaviour="stretch"
                />
                <TextInput
                    placeholder={`${localize("category-name-placeholder")} (EN)`}
                    title={`${localize("category-name")} (EN)`}
                    customColor={colors.project.brandWhite}
                    onChangeText={setNewCategoryTitleEN}
                    value={newCategoryTitleEN}
                    spreadBehaviour="stretch"
                />
                <TextInput
                    placeholder={localize("category-key-placeholder")}
                    customColor={colors.project.brandWhite}
                    title={localize("category-key")}
                    onChangeText={setNewCategoryKey}
                    spreadBehaviour="stretch"
                    value={newCategoryKey}
                />
            </View>
        </Dialog>;
    };

    const renderDeleteDialog = () => {
        return <Dialog
            primaryButtonProps={{
                icon: ({
                    color,
                    size
                }) => <Trash2Icon
                    color={colors.content.icon[color as never] || color}
                    size={size}
                />,
                title: localize("delete") || "Sil",
                isLoading: isSubmitting,
                onPress: handleDelete,
                type: "danger"
            }}
            secondaryButtonProps={{
                onPress: () => setIsDeleteDialogVisible(false),
                customTextColor: colors.project.brandWhite,
                customBorderColor: "secondaryButton",
                title: localize("cancel") || "İptal",
                customColor: "secondaryButton",
                variant: "filled"
            }}
            title={localize("delete-category-confirm-title")}
            onClosed={() => setIsDeleteDialogVisible(false)}
            isVisible={isDeleteDialogVisible}
            variant="yes-no"
        >
            <View
                style={{
                    gap: spaces.spacingMd
                }}
            >
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {localize("delete-category-confirm-desc")}
                </Text>
            </View>
        </Dialog>;
    };

    const renderList = () => {
        if (isLoading && categories.length === 0) {
            return <View
                style={stylesheet.emptyContainer}
            >
                <ActivityIndicator
                    color={colors.project.gold}
                    size="large"
                />
            </View>;
        }

        if (categories.length === 0) {
            return <View
                style={stylesheet.emptyContainer}
            >
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {localize("no-category-found")}
                </Text>
            </View>;
        }

        return <View>
            <View
                style={[
                    stylesheet.listContainer,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                {categories.map((category) => {
                    const primaryTitle = activeLocale === "en-US"
                        ? (category.titleEN || category.titleTR || category.key)
                        : (category.titleTR || category.titleEN || category.key);
                    const secondaryTitle = activeLocale === "en-US"
                        ? category.titleTR
                        : category.titleEN;
                    const secondaryLabel = activeLocale === "en-US" ? "TR" : "EN";

                    return <View
                        style={[
                            stylesheet.categoryItemWrapper,
                            {
                                backgroundColor: colors.project.brandWhite,
                                padding: spaces.spacingMd,
                                borderRadius: radiuses.md
                            }
                        ]}
                        key={category._id}
                    >
                        <View
                            style={stylesheet.categoryItem}
                        >
                            <View
                                style={stylesheet.categoryInfoContainer}
                            >
                                <Text
                                    variant="bodyMediumSize"
                                >
                                    {primaryTitle}
                                </Text>
                                <View
                                    style={stylesheet.categorySubInfoContainer}
                                >
                                    {Boolean(secondaryTitle) && <Text
                                        customColor={colors.content.text.mid}
                                        variant="bodySmallSize"
                                    >
                                        {`${secondaryLabel}: ${secondaryTitle}`}
                                    </Text>}
                                    <Text
                                        customColor={colors.content.text.mid}
                                        variant="bodySmallSize"
                                    >
                                        {`Key: ${category.key}`}
                                    </Text>
                                </View>
                            </View>
                            <View
                                style={{
                                    flexDirection: "row",
                                    gap: spaces.spacingSm
                                }}
                            >
                                <Button
                                    onPress={() => {
                                        setEditingCategoryTitleTR(category.titleTR || "");
                                        setEditingCategoryTitleEN(category.titleEN || "");
                                        setEditingCategoryKey(category.key);
                                        setSelectedCategoryID(category._id);
                                        setIsEditDialogVisible(true);
                                    }}
                                    icon={({
                                        size
                                    }) => <PencilIcon
                                        color={colors.project.brandWhite}
                                        size={size}
                                    />}
                                    customBorderColor="secondaryButton"
                                    customColor="secondaryButton"
                                    spreadBehaviour="free"
                                    variant="filled"
                                />
                                <Button
                                    onPress={() => {
                                        setSelectedCategoryID(category._id);
                                        setIsDeleteDialogVisible(true);
                                    }}
                                    icon={({
                                        color,
                                        size
                                    }) => <Trash2Icon
                                        color={colors.content.icon[color]}
                                        size={size}
                                    />}
                                    spreadBehaviour="free"
                                    type="danger"
                                />
                            </View>
                        </View>
                    </View>;
                })}
            </View>
            {totalCount > 0 && <View
                style={{
                    marginTop: spaces.spacingLg
                }}
            >
                <Pagination
                    onSelect={(item) => {
                        setPage(item.pageNumber);
                        fetchCategories(item.pageNumber);
                    }}
                    totalDataCount={totalCount}
                    currentPage={page}
                    itemPerPage={20}
                />
            </View>}
        </View>;
    };

    const renderEditDialog = () => {
        return <Dialog
            contentContainerStyle={stylesheet.dialogContentContainer}
            secondaryButtonProps={{
                onPress: () => setIsEditDialogVisible(false),
                customTextColor: colors.project.brandWhite,
                customBorderColor: "secondaryButton",
                title: localize("cancel") || "İptal",
                customColor: "secondaryButton",
                variant: "filled"
            }}
            onClosed={() => setIsEditDialogVisible(false)}
            primaryButtonProps={{
                title: localize("save") || "Kaydet",
                isLoading: isSubmitting,
                onPress: handleEdit
            }}
            title={localize("edit-category")}
            isVisible={isEditDialogVisible}
            variant="yes-no"
        >
            <View
                style={[
                    stylesheet.dialogForm,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <TextInput
                    placeholder={`${localize("category-name-placeholder")} (TR)`}
                    title={`${localize("category-name")} (TR)`}
                    onChangeText={setEditingCategoryTitleTR}
                    customColor={colors.project.brandWhite}
                    value={editingCategoryTitleTR}
                    spreadBehaviour="stretch"
                />
                <TextInput
                    placeholder={`${localize("category-name-placeholder")} (EN)`}
                    title={`${localize("category-name")} (EN)`}
                    onChangeText={setEditingCategoryTitleEN}
                    customColor={colors.project.brandWhite}
                    value={editingCategoryTitleEN}
                    spreadBehaviour="stretch"
                />
                <TextInput
                    placeholder={localize("category-key-placeholder")}
                    customColor={colors.project.brandWhite}
                    onChangeText={setEditingCategoryKey}
                    title={localize("category-key")}
                    value={editingCategoryKey}
                    spreadBehaviour="stretch"
                />
            </View>
        </Dialog>;
    };

    return <PageContainer
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl,
                    gap: spaces.spacingXl
                }
            ]}
        >
            {renderHeader()}
            {renderFilters()}
            {renderList()}
        </View>
        {renderCreateDialog()}
        {renderDeleteDialog()}
        {renderEditDialog()}
    </PageContainer>;
};

export default SettingsNewsCategories;
